import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Network, Sparkles } from 'lucide-react';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  initialsFromName,
} from '@mlm/ui';
import {
  getEarningsBreakdown,
  getGiveMeProgress,
  getLedger,
  getMe,
  getMonthlyPv,
  getRankProgress,
  getSlotStanding,
  getSlotWallet,
  getTenantConfig,
  getTotalSaved,
  getUnplacedDownline,
  listMyCodes,
} from '../lib/api';
import { KitGrid, useActivationKits } from '../components/kit-grid';
import { useBrand } from '../storefront/lib/hooks';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { ActivationLadder } from '../components/activation-ladder';
import { WalletPanel, WalletPanelEmpty } from '../components/wallet/wallet-panel';
import { EarningsBreakdownPanel } from '../components/wallet/earnings-breakdown-panel';
import { StatementList } from '../components/wallet/statement-list';
import { StandingPanel } from '../components/wallet/standing-panel';
import { RankProgressPanel } from '../components/wallet/rank-progress-panel';
import { GiveMePanel } from '../components/wallet/give-me-panel';
import { PvMonthlyPanel } from '../components/wallet/pv-monthly-panel';
import { BinaryPointsHistoryPanel } from '../components/wallet/binary-points-history-panel';
import { PendingOrdersPanel } from '../components/dashboard/pending-orders-panel';
import { AnnouncementsPanel } from '../components/dashboard/announcements-panel';
import { TotalSavedPanel } from '../components/dashboard/total-saved-panel';

/**
 * Member home, a passbook. The onboarding tutorial (ActivationLadder) leads and
 * is the centerpiece for a new member. Once a slot is active, the body is a
 * wallet+standing rail beside the statement, with contextual Create/Place slot
 * actions when there's actually something to act on. Navigation stays in the
 * sidebar — these are actions, not a second nav.
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const {
    slots,
    hasAnyActiveSlot,
    activeSlotLabel,
    activeSlotId,
    isLoading: slotsLoading,
  } = useMySlots();
  const slotReady = !slotsLoading && hasAnyActiveSlot;
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });

  const walletQ = useQuery({
    queryKey: ['wallet', activeSlotId],
    queryFn: () => getSlotWallet(activeSlotId),
    enabled: slotReady,
  });
  const ledgerQ = useQuery({
    queryKey: ['ledger', activeSlotId, 1, 8],
    queryFn: () => getLedger({ slotId: activeSlotId ?? undefined, pageSize: 8 }),
    enabled: slotReady,
  });
  const standingQ = useQuery({
    queryKey: ['standing', activeSlotId],
    queryFn: () => getSlotStanding(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const earningsBreakdownQ = useQuery({
    queryKey: ['earnings-breakdown', activeSlotId],
    queryFn: () => getEarningsBreakdown(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const rankProgressQ = useQuery({
    queryKey: ['rank-progress', activeSlotId],
    queryFn: () => getRankProgress(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const giveMeQ = useQuery({
    queryKey: ['give-me-progress', activeSlotId],
    queryFn: () => getGiveMeProgress(activeSlotId!),
    enabled: slotReady && !!activeSlotId,
  });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  // Placement only exists to feed the binary tree, so with the plan off there's
  // nothing to place — and the genealogy page hides its Binary tab, which is
  // where the "Place slot" button leads.
  const binaryOn =
    !!meQ.data && !(meQ.data.tenantPolicy?.disabledPlanCodes ?? []).includes('binary');
  const unplacedQ = useQuery({
    queryKey: ['unplaced-downline', activeSlotId],
    queryFn: () => getUnplacedDownline(activeSlotId!),
    enabled: slotReady && !!activeSlotId && binaryOn,
  });
  const memberPvDisplay = meQ.data?.tenantPolicy?.memberPvDisplay ?? 'off';
  const pvMonthlyQ = useQuery({
    queryKey: ['pv-monthly', activeSlotId],
    queryFn: () => getMonthlyPv(activeSlotId!),
    enabled: slotReady && !!activeSlotId && memberPvDisplay !== 'off',
  });
  const unredeemedQ = useQuery({
    queryKey: ['my-codes-available'],
    queryFn: () => listMyCodes({ onlyAvailable: true }),
  });
  // Member-level (all slots) — keyed on nothing slot-specific. Fires only when
  // the tenant flag says the card exists; the endpoint re-gates server-side.
  const totalSavedQ = useQuery({
    queryKey: ['total-saved'],
    queryFn: getTotalSaved,
    enabled: tenantQ.data?.totalSavedCounter === true,
  });

  if (slotsLoading || !user) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-56 lg:col-span-1" />
          <Skeleton className="h-56 lg:col-span-2" />
        </div>
      </div>
    );
  }

  const disabledPlanCodes = meQ.data?.tenantPolicy?.disabledPlanCodes;
  const unredeemedCount = unredeemedQ.data?.length ?? 0;
  const unplacedCount = unplacedQ.data?.length ?? 0;
  // Slot count toward the tenant limit excludes cancelled slots (they free a
  // slot), mirroring the createSlot enforcement.
  const slotCount = slots.filter((s) => s.status !== 'cancelled').length;
  const slotLimit = tenantQ.data?.maxSlotsPerMember ?? null;

  const greetingName = user.firstName ?? user.fullName ?? user.email;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Avatar
          size="lg"
          className="h-12 w-12"
          src={user.profilePictureUrl ?? undefined}
          initials={initialsFromName(user.fullName ?? user.email, 'ME')}
        />
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold">Hi, {greetingName}</div>
          <div className="text-sm text-muted-foreground">Welcome back to your dashboard.</div>
        </div>
      </div>

      <ActivationLadder />

      <AnnouncementsPanel />

      {hasAnyActiveSlot ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1 text-sm"
              title="Slots you own toward your tenant slot limit"
            >
              <Network className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Slots</span>
              <span className="font-semibold tabular-nums">
                {slotCount}
                {slotLimit != null ? ` / ${slotLimit}` : ''}
              </span>
            </span>
            <SlotActions unredeemedCount={unredeemedCount} unplacedCount={unplacedCount} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-1">
              {walletQ.data ? (
                <WalletPanel
                  wallet={walletQ.data}
                  slotLabel={activeSlotLabel}
                  topupPayoffHref={
                    meQ.data?.tenantPolicy?.walletTopupEnabled &&
                    meQ.data?.tenantPolicy?.cdTopupSettlesDebt
                      ? '/wallet-topup'
                      : null
                  }
                />
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <Skeleton className="h-40 w-full" />
                  </CardContent>
                </Card>
              )}
              {tenantQ.data?.totalSavedCounter &&
              totalSavedQ.data?.enabled &&
              totalSavedQ.data.totals ? (
                <TotalSavedPanel totals={totalSavedQ.data.totals} />
              ) : null}
              {standingQ.data ? (
                <StandingPanel
                  standing={standingQ.data}
                  disabledPlanCodes={disabledPlanCodes}
                  showSponsor={tenantQ.data?.showSponsorOnDashboard}
                />
              ) : null}
              {earningsBreakdownQ.data ? (
                <EarningsBreakdownPanel
                  entries={earningsBreakdownQ.data}
                  slotId={activeSlotId}
                  kindLabels={meQ.data?.ledgerLabels}
                  planLabels={meQ.data?.ledgerSourcePlanLabels}
                  legacyOffset={walletQ.data?.legacyEarnedOffset}
                />
              ) : null}
              {(standingQ.data?.binaryVariant === 'legacy' ||
                standingQ.data?.binaryVariant === 'extreme') &&
              activeSlotId ? (
                <BinaryPointsHistoryPanel slotId={activeSlotId} />
              ) : null}
              {memberPvDisplay !== 'off' && pvMonthlyQ.data ? (
                <PvMonthlyPanel
                  buckets={pvMonthlyQ.data.buckets}
                  showGpv={memberPvDisplay === 'ppv_and_gpv'}
                />
              ) : null}
              {rankProgressQ.data?.stairstepActive && rankProgressQ.data.progress ? (
                <RankProgressPanel progress={rankProgressQ.data.progress} />
              ) : null}
              {giveMeQ.data?.planActive && giveMeQ.data.rows.length > 0 ? (
                <GiveMePanel rows={giveMeQ.data.rows} title={giveMeQ.data.planLabel} />
              ) : null}
              <PendingOrdersPanel slotId={activeSlotId} />
            </div>
            <div className="lg:col-span-2">
              {ledgerQ.isLoading ? (
                <Card>
                  <CardContent className="p-6">
                    <Skeleton className="h-48 w-full" />
                  </CardContent>
                </Card>
              ) : (
                <StatementList
                  entries={ledgerQ.data ?? []}
                  kindLabels={meQ.data?.ledgerLabels}
                  planLabels={meQ.data?.ledgerSourcePlanLabels}
                  subtitle={activeSlotLabel ?? undefined}
                  viewAllHref="/earnings"
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <NoSlotKitsSection />
          <WalletPanelEmpty />
        </>
      )}
    </div>
  );
}

/**
 * Pre-slot members need a path to their first kit. The body is the shared
 * `KitGrid` — the same component as the Browse-kits `KitPickerDialog`, so the
 * two surfaces can't drift (their Buy Now behaviour once did: `add` vs
 * `addOnly`). Auto-hides when no kits are available (admin hasn't set any up
 * yet) via `useActivationKits`.
 */
function NoSlotKitsSection() {
  const brand = useBrand();
  const { kits, isLoading } = useActivationKits();
  if (!isLoading && kits.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" /> Pick your {brand.activationKitLabel}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!isLoading && (
          <p className="mb-3 text-sm text-muted-foreground">
            Buying a {brand.kitLabel} creates a Code + PIN you redeem to activate your first slot.
          </p>
        )}
        <KitGrid />
      </CardContent>
    </Card>
  );
}

/**
 * Contextual actions, not navigation: shown only when there's something to do.
 * Each links to the proven flow (Vault redeem / genealogy seat-picker).
 */
function SlotActions({
  unredeemedCount,
  unplacedCount,
}: {
  unredeemedCount: number;
  unplacedCount: number;
}) {
  if (unredeemedCount === 0 && unplacedCount === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {unredeemedCount > 0 ? (
        <Button asChild>
          <Link to="/code-vault">
            <KeyRound className="h-4 w-4" /> Create slot
            <span className="ml-1 rounded bg-primary-foreground/20 px-1.5 text-xs tabular-nums">
              {unredeemedCount}
            </span>
          </Link>
        </Button>
      ) : null}
      {unplacedCount > 0 ? (
        <Button asChild variant="outline">
          <Link to="/genealogy">
            <Network className="h-4 w-4" /> Place slot
            <span className="ml-1 rounded bg-muted px-1.5 text-xs tabular-nums">
              {unplacedCount}
            </span>
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
